import { useState, useEffect } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import FieldDefinition from "../dynamicsFields/FieldDefinition";
import { TIPOS_DATO } from "../dynamicsFields/fieldTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareMinus, faSquarePlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import EditModal from "../ui/EditModal.js";

const AddMedicacionPorPatologia = () => {
  const [Patologias, setPatologias] = useState([]);
  const [Medicaciones, setMedicaciones] = useState([]);
  const [IdPatologia, setIdPatologia] = useState("");
  const [IdMedicacion, setIdMedicacion] = useState("");
  const [MedicacionPorPatologia, setMedicacionPorPatologia] = useState([]);
  const [fieldDefinition, setFieldDefinition] = useState({});
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [medicacionAEditar, setMedicacionAEditar] = useState(null);

  useEffect(() => {
    getPatologias();
    getMedicaciones();
  }, []);

  useEffect(() => {
    if (IdPatologia) {
      setIdMedicacion("");
      setFieldDefinition({});
      cargarMedicacionesPorPatologia(IdPatologia);
    }
  }, [IdPatologia]);

  const getPatologias = async () => {
    try {
      const res = await api.get("/patologias");
      setPatologias(res.data.filter(p => p.estado === 1));
    } catch (error) {
      console.error("Error al obtener patologías:", error);
    }
  };

  const getMedicaciones = async () => {
    try {
      const res = await api.get("/medicaciones");
      setMedicaciones(res.data.filter(m => m.Estado === 1));
    } catch (error) {
      console.error("Error al obtener medicaciones:", error);
    }
  };

  const cargarMedicacionesPorPatologia = async (idPatologia) => {
    try {
      const res = await api.get("/medicaciones-por-patologia");
      const filtradas = res.data
        .filter(mp => mp.IdPatologia === idPatologia)
        .map(mp => ({
          ...mp,
          NombreMedicacion: Medicaciones.find(m => m.IdMedicacion === mp.IdMedicacion)?.Nombre || "Desconocida"
        }));
      setMedicacionPorPatologia(filtradas);
    } catch (error) {
      console.error("Error al obtener medicaciones por patología:", error);
    }
  };

  const guardarMedicacionPorPatologia = async (e) => {
    e.preventDefault();

    if (!IdPatologia || !IdMedicacion) {
      return Swal.fire("Campos incompletos", "Debe seleccionar patología y medicación", "warning");
    }

    // Verificar si ya existe la relación
    const yaExiste = MedicacionPorPatologia.some(mp => mp.IdMedicacion === IdMedicacion);
    if (yaExiste) {
      return Swal.fire("Duplicado", "Esta medicación ya está asociada a la patología seleccionada.", "error");
    }

    if (!fieldDefinition.tipo) {
      return Swal.fire("Configuración incompleta", "Debe configurar los campos", "warning");
    }

    if ([TIPOS_DATO.SELECCION].includes(fieldDefinition.tipo) &&
        (!fieldDefinition.configuracion?.opciones?.length)) {
      return Swal.fire("Configuración incompleta", "Debe agregar opciones", "warning");
    }

    try {
      await api.post("/medicaciones-por-patologia", {
        IdMedicacion,
        IdPatologia,
        json_definicion: fieldDefinition
      });

      Swal.fire("Éxito", "Medicación asociada a la patología", "success");
      cargarMedicacionesPorPatologia(IdPatologia);
      setIdMedicacion("");
      setFieldDefinition({});
    } catch (error) {
      console.error("Error al guardar relación:", error);
      if (error.response?.status === 409) {
        Swal.fire("Duplicado", error.response.data.msg || "Esta medicación ya está asociada a la patología.", "error");
      } else {
        Swal.fire("Error", "No se pudo asociar la medicación", "error");
      }
    }
  };

  const toggleEstado = async (id) => {
    try {
      await api.patch(`/medicacion-por-patologia/${id}/estado`);
      cargarMedicacionesPorPatologia(IdPatologia);
      Swal.fire("Actualizado", "Estado actualizado correctamente", "success");
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      Swal.fire("Error", "No se pudo cambiar el estado", "error");
    }
  };

  const actualizarMedicacion = async (valores) => {
    try {
      // Validar que el tipo de dato esté configurado
      if (!valores.json_definicion || !valores.json_definicion.tipo) {
        Swal.fire("Configuración incompleta", "Debe configurar el tipo de dato para la medicación.", "warning");
        return;
      }

      // Si es de tipo selección, validar que tenga opciones
      if (
        [TIPOS_DATO.SELECCION, TIPOS_DATO.MULTIPLE].includes(valores.json_definicion.tipo) &&
        (!valores.json_definicion.configuracion?.opciones || valores.json_definicion.configuracion.opciones.length === 0)
      ) {
        Swal.fire("Configuración incompleta", "Para tipos de selección, debe agregar al menos una opción.", "warning");
        return;
      }

      // Actualizar la medicación (nombre y descripción)
      await api.put(`/medicaciones/${valores.IdMedicacion}`, {
        Nombre: valores.Nombre,
        Descripcion: valores.Descripcion || "",
      });

      // Actualizar la relación medicación-patología con el nuevo json_definicion
      if (IdPatologia) {
        await api.put(`/medicaciones-por-patologia/actualizar-json-definicion`, {
          IdPatologia: parseInt(IdPatologia),
          IdMedicacion: valores.IdMedicacion,
          json_definicion: valores.json_definicion
        });
      }

      Swal.fire("Actualizado", "La medicación fue actualizada correctamente.", "success");
      setEditModalOpen(false);
      cargarMedicacionesPorPatologia(IdPatologia);
      getMedicaciones();
    } catch (error) {
      console.error("Error al actualizar medicación:", error);
      Swal.fire("Error", "No se pudo actualizar la medicación.", "error");
    }
  };

  const columnas = [
    {
      name: "Medicación",
      selector: row => row.NombreMedicacion || "Desconocida",
      sortable: true,
      width: "40%",
    },
    {
      name: "Tipo de campo",
      selector: row => { let def = row.json_definicion || {}; if (typeof def === 'string') { try { def = JSON.parse(def); } catch { def = {}; } } return def.tipo || "No configurado"; },
      width: "30%",
    },
    {
      name: "Acciones",
      cell: row => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            className="button is-small is-info"
            title="Editar"
            onClick={() => {
              const medOriginal = Medicaciones.find(m => m.IdMedicacion === row.IdMedicacion);
              setMedicacionAEditar({
                IdMedicacion: row.IdMedicacion,
                IdMedicacionPorPatologia: row.IdMedicacionPorPatologia,
                Nombre: medOriginal?.Nombre || row.NombreMedicacion || "",
                Descripcion: medOriginal?.Descripcion || "",
                json_definicion: (() => { const raw = row.json_definicion || { tipo: "", configuracion: {} }; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return { tipo: "", configuracion: {} }; } } return raw; })(),
              });
              setEditModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
          {row.estado === 1 ? (
            <FontAwesomeIcon
              icon={faSquarePlus}
              style={{ color: "#37ff00", cursor: "pointer" }}
              size="2xl"
              title="Desactivar"
              onClick={() => toggleEstado(row.IdMedicacionPorPatologia)}
            />
          ) : (
            <FontAwesomeIcon
              icon={faSquareMinus}
              style={{ color: "#ff0000", cursor: "pointer" }}
              size="2xl"
              title="Activar"
              onClick={() => toggleEstado(row.IdMedicacionPorPatologia)}
            />
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "30%",
      center: true,
    },
  ];

  const filtrarPorEstado = () => {
    if (filtroEstado === "todas") return MedicacionPorPatologia;
    if (filtroEstado === "activas") return MedicacionPorPatologia.filter(v => v.estado === 1);
    if (filtroEstado === "inactivas") return MedicacionPorPatologia.filter(v => v.estado === 0);
    return MedicacionPorPatologia;
  };

  return (
    <div className="columns is-centered">
      <div className="column is-half">
        <div className="notification is-primary">
          <strong>Medicación por Patología</strong>
        </div>

        <form onSubmit={guardarMedicacionPorPatologia}>
          <div className="field">
            <label className="label">Patología</label>
            <div className="select is-fullwidth">
              <select
                value={IdPatologia}
                onChange={e => setIdPatologia(parseInt(e.target.value))}
              >
                <option value="">Seleccionar</option>
                {Patologias.map(p => (
                  <option key={p.IdPatologia} value={p.IdPatologia}>
                    {p.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label">Medicación</label>
            <div className="select is-fullwidth">
              <select
                value={IdMedicacion}
                onChange={e => setIdMedicacion(parseInt(e.target.value))}
              >
                <option value="">Seleccionar</option>
                {Medicaciones.map(m => (
                  <option key={m.IdMedicacion} value={m.IdMedicacion}>
                    {m.Nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {IdPatologia && IdMedicacion && (
            <FieldDefinition
              inicial={fieldDefinition}
              onChange={setFieldDefinition}
            />
          )}

          <button className="button is-success mt-4">Guardar</button>
        </form>

        <div style={{ display: "flex", gap: "10px", margin: "1rem 0" }}>
          {["todas", "activas", "inactivas"].map(estado => (
            <button
              key={estado}
              type="button"
              onClick={() => setFiltroEstado(estado)}
              className={`button is-small ${filtroEstado===estado?'is-primary':'is-light'}`}
            >
              {estado.charAt(0).toUpperCase() + estado.slice(1)}
            </button>
          ))}
        </div>

        <DataTable
          columns={columnas}
          data={filtrarPorEstado()}
          pagination
          noDataComponent="No hay medicaciones configuradas"
        />

        {editModalOpen && (
          <EditModal
            title="Editar Medicación"
            initialValues={medicacionAEditar}
            fields={[
              { name: "Nombre", label: "Nombre", type: "text", required: true },
              { name: "Descripcion", label: "Descripción", type: "textarea", required: false },
              {
                name: "json_definicion",
                label: "Configuración del campo",
                type: "custom",
                required: true,
                component: FieldDefinition,
                componentProps: {
                  allSymptoms: Medicaciones,
                  sintomasPorPatologia: MedicacionPorPatologia,
                },
              },
            ]}
            onSubmit={actualizarMedicacion}
            onClose={() => setEditModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AddMedicacionPorPatologia;
